import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Copy, Sparkles, Tag, Gift, Loader2, Scissors, CalendarCheck, Wallet } from "lucide-react";
import { supabase } from "@/lib/supabase";
import Input from "@/components/ui/Input";
import { validateEmail } from "@/lib/validator";
import { LOGO_URL } from "@/constants";

const ReferralVoucher: React.FC = () => {
    const [email, setEmail] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [voucherCode, setVoucherCode] = useState<string | null>(null);

    const generateVoucher = async () => {
        const validation = validateEmail(email);
        if (!validation.isValid) {
            toast.error("Inserisci un indirizzo email valido.");
            return;
        }

        setLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('generate-referral-voucher', {
                body: { email }
            });

            if (error) {
                const errorDetails = await error.context?.json().catch(() => ({ error: 'unknown' }));

                switch (errorDetails.error) {
                    case 'email_already_exists':
                        toast.error("Voucher già richiesto! Controlla la tua email.");
                        break;
                    case 'rate_limit_exceeded':
                        toast.error("Troppi tentativi. Attendi un'ora.");
                        break;
                    default:
                        toast.error("Errore durante la generazione. Riprova.");
                }
                return;
            }

            setVoucherCode(data.code);
            toast.success("Voucher generato! Diventa un ambasciatore Clip.");
        } catch (err) {
            console.error("Referral Error:", err);
            toast.error("Errore di connessione.");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (voucherCode) {
            navigator.clipboard.writeText(voucherCode);
            toast.success("Codice copiato!");
        }
    };

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-clip-100 relative overflow-hidden flex items-center justify-center py-4">
            {/* Background Decorativo */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <motion.div
                    animate={{ x: [0, 50, 0], scale: [1, 1.1, 1], opacity: [0.3, 0.4, 0.3] }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-[-5%] left-[-5%] w-[400px] h-[400px] bg-clip-100 rounded-full blur-[80px]"
                />
            </div>

            <nav className="absolute top-0 left-0 right-0 p-6 flex justify-center z-50">
                <img src={LOGO_URL} alt="Clip Logo" className="h-8 w-auto" />
            </nav>

            <div className="relative z-10 w-full max-w-2xl px-6 mt-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/90 backdrop-blur-2xl border border-gray-100 shadow-2xl rounded-[2.5rem] p-8 md:p-12 text-center"
                >
                    <AnimatePresence mode="wait">
                        {!voucherCode ? (
                            <motion.div
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <header className="mb-10 text-center">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-clip-50 text-clip-700 rounded-full text-sm font-bold mb-6">
                                        <Sparkles size={16} />
                                        CLIP REFERRAL PROGRAM
                                    </div>
                                    <h1 className="text-4xl font-black text-gray-900 mb-4 leading-[1.1]">
                                        Porta il tuo salone <br /><span className="text-clip-500">nel futuro.</span>
                                    </h1>
                                    <p className="text-gray-600 text-lg max-w-md mx-auto leading-relaxed">
                                        Clip è l'app che semplifica la vita ai professionisti della bellezza. Aiuta il tuo salone di fiducia a digitalizzarsi.
                                    </p>
                                </header>

                                {/* Spiegazione Step-by-Step */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 text-left">
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3 text-clip-500 font-bold">1</div>
                                        <h3 className="font-bold text-gray-900 mb-1">Crea</h3>
                                        <p className="text-xs text-gray-500 leading-snug">Genera il tuo voucher inserendo la tua email.</p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3 text-clip-500 font-bold">2</div>
                                        <h3 className="font-bold text-gray-900 mb-1">Converti</h3>
                                        <p className="text-xs text-gray-500 leading-snug">Manda il codice al salone. Se si iscrive, ottiene uno <b>sconto del 10%</b>.</p>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3 text-clip-500 font-bold">3</div>
                                        <h3 className="font-bold text-gray-900 mb-1">Guadagna</h3>
                                        <p className="text-xs text-gray-500 leading-snug">Dopo il primo addebito, riceverai un buono Amazon di <b>30 euro</b>!</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <Input
                                        placeholder="Inserisci la tua email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="h-14 text-md text-center border-gray-200 focus:border-clip-400 focus:ring-4 focus:ring-clip-100 transition-all"
                                    />
                                    <button
                                        onClick={generateVoucher}
                                        disabled={loading}
                                        className="w-full h-16 bg-gray-900 text-white rounded-2xl font-black text-lg hover:bg-black hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-gray-200 disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                        {loading ? <Loader2 className="animate-spin" /> : <Gift size={22} />}
                                        Genera il Voucher
                                    </button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="success"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="space-y-8"
                            >
                                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                                    <CalendarCheck size={40} />
                                </div>

                                <div>
                                    <h2 className="text-3xl font-black text-gray-900 mb-2">Sei ufficialmente un<br /> <span className="text-clip-500">Ambassador</span></h2>
                                    <p className="text-gray-500">Condividi questo codice con il proprietario del tuo salone.</p>
                                </div>

                                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-6 mb-8 relative group hover:border-clip-300 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <Tag className="text-gray-400 w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                                        <span className="flex-1 font-mono text-xl md:text-2xl text-center font-black text-gray-900 tracking-wider">
                                            {voucherCode}
                                        </span>
                                        <button
                                            onClick={copyToClipboard}
                                            className="p-3 bg-white border border-gray-200 hover:border-clip-500 hover:text-clip-600 rounded-xl transition-all shadow-sm hover:shadow-md"
                                            title="Copia codice"
                                        >
                                            <Copy size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-purple-50 rounded-2xl p-6 text-left border border-purple-100 flex gap-4">
                                    <Wallet className="text-purple-600 flex-shrink-0" size={24} />
                                    <div>
                                        <h4 className="font-bold text-purple-900 mb-1">Il tuo premio ti aspetta</h4>
                                        <p className="text-sm text-purple-800 leading-relaxed">
                                            Una volta che il salone effettuerà il suo primo addebito su Clip usando questo codice, riceverai una notifica via email per riscattare il tuo <strong>bonus fedeltà</strong>.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                <p className="text-center text-gray-400 text-sm mt-8">
                    Vuoi saperne di più? Visita <a href="https://www.clipbooking.com/" className="underline hover:text-clip-500 transition-colors">clipbooking.com</a>
                </p>
            </div>
        </div>
    );
};

export default ReferralVoucher;